<script lang="ts">
	import { createEventDispatcher, onMount, tick } from 'svelte';
	import { format } from 'svelte-i18n';
	import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
	import { scenes } from 'src/stores/lists/scenes';

	export let suggestedDeviceIds: Set<string> = new Set();
	export let mentionActive = false;
	export let filteredItems: QuickItem[] = [];
	export let tokenFilter: 'room' | 'device' | 'group' | 'scene' | null = null;

	const dispatch = createEventDispatcher<{
		mention: { name: string; type: MentionType };
		close: void;
	}>();

	type MentionType = 'room' | 'device' | 'group' | 'scene';

	interface QuickItem {
		id: string | number;
		name: string;
		type: MentionType;
		icon: string;
		roomName?: string;
		roomId?: string | number;
	}

	const typeLabels: Record<MentionType, string> = {
		room: 'Rooms:',
		device: 'Devices:',
		group: 'Groups:',
		scene: 'Scenes:',
	};

	let scrollEl: HTMLDivElement;
	let showLeftFade = false;
	let showRightFade = false;
	let focusedChipIndex = -1;

	function toQuickItems(
		source: Record<string, any> | Array<any>,
		type: MentionType,
		icon: string,
		limit: number,
		nameExtractor?: (entry: [string, any]) => { id: string; name: string },
	): QuickItem[] {
		if (nameExtractor) {
			return Object.entries(source)
				.slice(0, limit === Infinity ? undefined : limit)
				.map(([id, s]) => {
					const extracted = nameExtractor([id, s]);
					return { ...extracted, type, icon };
				});
		}
		return Object.values(source)
			.slice(0, limit === Infinity ? undefined : limit)
			.map((v: any) => ({
				id: String(v.id),
				name: v.name,
				type,
				icon,
			}));
	}

	$: allItems = [
		...toQuickItems($rooms, 'room', 'fas fa-door-open', 3),
		...toQuickItems($devices, 'device', 'fas fa-plug', 5),
		...toQuickItems($groups, 'group', 'fas fa-layer-group', 3),
		...toQuickItems($scenes, 'scene', 'fas fa-bolt', 3, ([id, s]) => ({
			id,
			name: s?._meta?.name || id,
		})),
	];

	$: allItemsUnlimited = [
		...toQuickItems($rooms, 'room', 'fas fa-door-open', Infinity),
		...toQuickItems($devices, 'device', 'fas fa-plug', Infinity),
		...toQuickItems($groups, 'group', 'fas fa-layer-group', Infinity),
		...toQuickItems($scenes, 'scene', 'fas fa-bolt', Infinity, ([id, s]) => ({
			id,
			name: s?._meta?.name || id,
		})),
	];

	$: filteredByType = tokenFilter
		? allItemsUnlimited.filter((item) => item.type === tokenFilter)
		: allItems;

	$: displayItems = mentionActive && filteredItems.length > 0 ? filteredItems : filteredByType;

	$: suggestionsLabel = tokenFilter
		? typeLabels[tokenFilter]
		: $format('ai_scene_chatbot_quick_label', { default: 'Suggested:' });

	$: if (mentionActive && filteredItems.length > 0) {
		focusedChipIndex = 0;
	} else {
		focusedChipIndex = -1;
	}

	$: hasItems = displayItems.length > 0;

	$: if (displayItems) {
		tick().then(checkFades);
	}

	onMount(() => {
		checkFades();
	});

	function checkFades() {
		if (!scrollEl) return;
		const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
		showLeftFade = scrollLeft > 0;
		showRightFade = scrollWidth > clientWidth + scrollLeft + 1;
	}

	function handleScroll() {
		checkFades();
	}

	function handleClick(item: QuickItem) {
		dispatch('mention', { name: item.name, type: item.type });
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (!hasItems) return;

		switch (e.key) {
			case 'Escape':
				e.preventDefault();
				e.stopPropagation();
				dispatch('close');
				break;
			case 'ArrowRight':
			case 'ArrowDown':
				e.preventDefault();
				focusedChipIndex = Math.min(focusedChipIndex + 1, displayItems.length - 1);
				scrollToFocusedChip();
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				e.preventDefault();
				focusedChipIndex = Math.max(focusedChipIndex - 1, 0);
				scrollToFocusedChip();
				break;
			case 'Enter':
				if (focusedChipIndex >= 0 && focusedChipIndex < displayItems.length) {
					e.preventDefault();
					handleClick(displayItems[focusedChipIndex]);
				}
				break;
		}
	}

	function scrollToFocusedChip() {
		tick().then(() => {
			if (!scrollEl) return;
			const chip = scrollEl.querySelector('.suggestion-chip.auto-focused') as HTMLElement;
			if (chip) {
				chip.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
			}
		});
	}
</script>

<svelte:window on:keydown={handleKeyDown} />

{#if hasItems}
	<div class="suggestions-container">
		<div
			class="suggestions-chips"
			bind:this={scrollEl}
			on:scroll={handleScroll}
			on:mousedown|preventDefault
			role="presentation"
		>
			<span class="suggestions-label">
				{suggestionsLabel}
			</span>
			{#each displayItems as item, i (item.type + item.id)}
				<button
					class="suggestion-chip {item.type}-chip"
					class:suggested={item.type === 'device' && suggestedDeviceIds.has(String(item.id))}
					class:auto-focused={mentionActive && i === focusedChipIndex}
					on:click={() => handleClick(item)}
					aria-label="Add {item.name} to message"
				>
					<i class={item.icon} aria-hidden="true"></i>
					{item.name}
				</button>
			{/each}
		</div>
		<div class="fade-overlay fade-left" class:visible={showLeftFade}></div>
		<div class="fade-overlay fade-right" class:visible={showRightFade}></div>
	</div>
{/if}

<style>
	.suggestions-container {
		position: relative;
		padding: 8px var(--spacing-m, 16px);
		/* border-top: 1px solid var(--color-border, #333); */
		flex-shrink: 0;
		overflow: hidden;
		touch-action: pan-x;
	}

	@media (min-width: 769px) {
		.suggestions-container {
			padding: 8px var(--spacing-l, 24px);
		}
	}

	.suggestions-chips {
		display: flex;
		gap: 8px;
		align-items: center;
		overflow-x: auto;
		overflow-y: hidden;
		scrollbar-width: none;
		touch-action: pan-x;
		overscroll-behavior: contain;
	}

	.suggestions-chips::-webkit-scrollbar {
		display: none;
	}

	.suggestions-label {
		flex-shrink: 0;
		font-size: 12px;
		font-weight: 300;
		line-height: 15px;
		color: rgba(255, 255, 255, 0.5);
		align-self: center;
	}

	.suggestion-chip {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px 12px;
		background: var(--color-background-3, #242527);
		border: 1px solid rgba(229, 231, 235, 0.2);
		border-radius: 24px;
		cursor: pointer;
		font-family: 'Inter', sans-serif;
		font-size: 13px;
		font-weight: 400;
		line-height: 16px;
		color: var(--color-text-primary, #fff);
		white-space: nowrap;
		transition:
			background-color 0.2s,
			border-color 0.2s;
	}

	.suggestion-chip:hover {
		background: var(--color-background-4, #3a3a3a);
		border-color: var(--color-primary-1);
	}

	.suggestion-chip i {
		font-size: 12px;
	}

	.room-chip i {
		color: var(--color-primary-1, #2196f3);
	}

	.device-chip i {
		color: #24d38c;
	}

	.group-chip i {
		color: var(--color-warning, #ffc107);
	}

	.scene-chip i {
		color: #9c27b0;
	}

	.room-chip.auto-focused {
		border-color: var(--color-primary-1);
		background: rgba(33, 150, 243, 0.15);
	}

	.device-chip.auto-focused {
		border-color: #24d38c;
		background: rgba(36, 211, 140, 0.15);
	}

	.group-chip.auto-focused {
		border-color: var(--color-warning, #ffc107);
		background: rgba(255, 193, 7, 0.15);
	}

	.scene-chip.auto-focused {
		border-color: #9c27b0;
		background: rgba(156, 39, 176, 0.15);
	}

	.suggestion-chip.suggested {
		border-color: var(--color-primary-1);
		background: rgba(33, 150, 243, 0.15);
		box-shadow: 0 0 0 1px rgba(33, 150, 243, 0.3);
	}

	.suggestion-chip.suggested i {
		color: var(--color-primary-1);
	}

	.fade-overlay {
		position: absolute;
		top: 0;
		bottom: 0;
		width: 40px;
		pointer-events: none;
		opacity: 0;
		transition: opacity 0.2s ease-in-out;
	}

	.fade-overlay.visible {
		opacity: 1;
	}

	.fade-left {
		left: 0;
		background: linear-gradient(
			90deg,
			var(--color-background-2, #151617) 0%,
			var(--color-background-2, #151617) 20%,
			rgba(21, 22, 23, 0) 100%
		);
	}

	.fade-right {
		right: 0;
		background: linear-gradient(
			270deg,
			var(--color-background-2, #151617) 0%,
			var(--color-background-2, #151617) 20%,
			rgba(21, 22, 23, 0) 100%
		);
	}
</style>
